import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { type ComponentProps, type ForwardedRef, forwardRef } from 'react'
import { cn } from '@/helpers/utils'

/**
 * @deprecated Legacy button styles. Use `Button` from
 * `@/components/ui/button` (ported from airbnb-elevate-fe) for new code.
 */
const buttonVariants = cva(
	'flex cursor-pointer items-center justify-center font-semibold transition focus-visible:shadow-airbnb-focus-visible disabled:cursor-not-allowed [&.disabled]:cursor-not-allowed',
	{
		variants: {
			variant: {
				primary:
					'bg-grey-1000 text-neutral-white hover:bg-neutral-black disabled:bg-grey-500 [&.disabled]:bg-grey-500',
				primarySuccess:
					'bg-success-600 text-neutral-white hover:bg-success-700 disabled:bg-success-200 disabled:text-neutral-white [&.disabled]:bg-success-200 [&.disabled]:text-neutral-white',
				primaryDanger:
					'bg-danger-600 text-neutral-white hover:bg-danger-700 disabled:bg-danger-200 disabled:text-neutral-white [&.disabled]:bg-danger-200 [&.disabled]:text-neutral-white',
				secondaryDanger:
					'bg-danger-100 text-danger-700 hover:bg-danger-200 active:bg-danger-200 disabled:bg-danger-100 disabled:text-danger-300 [&.disabled]:bg-danger-100 [&.disabled]:text-danger-300',
				primaryBlue:
					'bg-blue-500 text-neutral-white hover:bg-blue-600 disabled:bg-blue-200 disabled:text-neutral-white [&.disabled]:bg-blue-200 [&.disabled]:text-neutral-white',
				brand: 'relative overflow-hidden bg-linear-(--gradient-rausch) text-neutral-white disabled:bg-linear-[unset] disabled:bg-tertiary-bg disabled:text-neutral-white [&.disabled]:bg-linear-[unset] [&.disabled]:bg-tertiary-bg [&.disabled]:text-neutral-white',
				secondary:
					'bg-grey-200 text-grey-1000 hover:bg-grey-300 active:bg-grey-300 disabled:bg-grey-200 disabled:text-grey-500 [&.disabled]:bg-grey-200 [&.disabled]:text-grey-500',
				outline:
					'border border-neutral-300 bg-neutral-white text-neutral-900 hover:bg-neutral-100 active:bg-neutral-100 disabled:border-grey-300 disabled:text-grey-500 [&.disabled]:border-grey-300 [&.disabled]:text-grey-500',
				beigeSecondary:
					'bg-beige-400 text-beige-1000 disabled:text-grey-700 [&.disabled]:text-grey-700',
				icon: 'bg-grey-200 text-grey-1000 hover:bg-grey-300 active:bg-grey-300 disabled:bg-grey-200 disabled:text-grey-500 [&.disabled]:bg-grey-200 [&.disabled]:text-grey-500',
				tertiary:
					'text-grey-1000 underline hover:text-grey-1000 disabled:text-grey-500 [&.disabled]:text-grey-500'
			},
			size: {
				xs: 'h-7 gap-1 px-2.5 text-xs [&>span]:gap-1 [&>svg]:size-4',
				sm: 'h-9 gap-1 px-4 text-sm [&>span]:gap-1 [&>svg]:size-4',
				md: 'h-11 gap-1 px-5 text-base [&>span]:gap-1 [&>svg]:size-5',
				lg: 'h-13 gap-1.5 px-6 text-lg [&>span]:gap-1.5 [&>svg]:size-6',
				xl: 'h-14 gap-2.5 px-6 py-3.5 text-lg [&>span]:gap-2.5 [&>svg]:size-6'
			},
			shape: {
				rectangle: 'rounded-lg',
				square: 'flex-shrink-0 rounded-lg',
				rounded: 'rounded-full'
			}
		},
		compoundVariants: [
			{
				size: 'xs',
				shape: 'square',
				class: 'size-7 p-0'
			},
			{
				size: 'sm',
				shape: 'square',
				class: 'size-9 p-0'
			},
			{
				size: 'md',
				shape: 'square',
				class: 'size-11 p-0'
			},
			{
				size: 'lg',
				shape: 'square',
				class: 'size-13 p-0'
			},
			{
				size: 'xl',
				shape: 'square',
				class: 'size-14 p-0'
			}
		],
		defaultVariants: {
			variant: 'primary',
			size: 'md',
			shape: 'rectangle'
		}
	}
)

export type ButtonProps = ComponentProps<'button'> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean
	}

/**
 * @deprecated Legacy button. Use `Button` from `@/components/ui/button`
 * (ported from airbnb-elevate-fe) for new code.
 */
const ButtonLegacy = forwardRef(function ButtonLegacy(
	{ className, variant, size, shape, asChild = false, ...props }: ButtonProps,
	ref: ForwardedRef<HTMLButtonElement>
) {
	const Comp = asChild ? Slot : 'button'

	return (
		<Comp
			className={cn(buttonVariants({ variant, size, shape, className }))}
			data-slot='button'
			ref={ref}
			{...props}
		/>
	)
})

export { ButtonLegacy, buttonVariants }
